import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import LeadCaptureModal from './LeadCaptureModal';
import { useTheme } from '../context/ThemeContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-[0_1px_30px_rgba(0,0,0,0.04)]' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-18">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <span className="text-xl md:text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
              M DION <span className="text-primary">LIGHTING</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.name} className="relative">
                {link.dropdown ? (
                  <div 
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(link.name)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      data-testid={`nav-${link.name.toLowerCase()}-dropdown`}
                      className={`flex items-center gap-1 text-sm font-medium transition-all duration-300 px-3 py-2 rounded-full ${
                        link.path && location.pathname.startsWith(link.path)
                          ? 'text-foreground bg-muted'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      {link.name}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === link.name && (
                      <div className="absolute top-full left-0 pt-2 w-48 z-50">
                        <div className="bg-card/90 backdrop-blur-xl border border-border/50 rounded-xl shadow-lg py-2">
                          {link.dropdown.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              data-testid={`nav-dropdown-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                              className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                    className={`text-sm font-medium transition-all duration-300 px-3 py-2 rounded-full ${
                      location.pathname === link.path
                        ? 'text-foreground bg-muted'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            
            <button
              onClick={toggleDarkMode}
              data-testid="dark-mode-toggle"
              className="p-2 ml-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-300"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            
            <Button 
              onClick={() => setIsModalOpen(true)} 
              data-testid="nav-get-started-btn"
              className="ml-3 rounded-full px-6 shadow-lg shadow-primary/20 hover:shadow-primary/30 hover:scale-105 active:scale-95 transition-all duration-300"
            >
              Get a Quote
            </Button>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={toggleDarkMode}
              data-testid="mobile-dark-mode-toggle"
              className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-background/95 backdrop-blur-xl border-t border-border/50" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.dropdown ? (
                  <div>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === link.name ? null : link.name)}
                      className={`w-full flex items-center justify-between py-3 px-3 rounded-xl text-base font-medium transition-all duration-300 ${
                        link.path && location.pathname.startsWith(link.path)
                          ? 'text-foreground bg-muted/50'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === link.name && (
                      <div className="pl-4 space-y-1 pb-2">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className="block py-2.5 px-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/30"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className={`block py-3 px-3 rounded-xl text-base font-medium transition-all duration-300 ${
                      location.pathname === link.path
                        ? 'text-foreground bg-muted/50'
                        : 'text-muted-foreground'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <Button 
              onClick={() => {
                setIsMobileMenuOpen(false);
                setIsModalOpen(true);
              }} 
              data-testid="mobile-get-started-btn"
              className="w-full mt-4 rounded-full"
            >
              Get a Quote
            </Button>
          </div>
        </div>
      )}

      <LeadCaptureModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </nav>
  );
};

export default Navbar;
